import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './PublicReclamationForm.css';

const MAX_VIDEO_SECONDS = 8;

const PublicReclamationForm = () => {
  const { qrToken: paramToken } = useParams();
  const [searchParams] = useSearchParams();
  const qrToken = paramToken || searchParams.get('qr') || searchParams.get('token');

  const [loadingInfo, setLoadingInfo] = useState(true);
  const [equipmentInfo, setEquipmentInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Form states
  const [type, setType] = useState('reclamation');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  
  // Photo states
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Video states
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoRecordedSuccess, setVideoRecordedSuccess] = useState(false);

  // In-browser video recorder states
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' | 'user'

  const videoStreamRef = useRef(null);
  const videoLiveRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Default fallback categories
  const defaultReclamations = [
    'Panne matérielle / Hors service',
    'Bouton ou commande défectueux',
    'Bruit ou vibration anormale',
    'Coussin / Assise endommagé',
    'Problème d\'hygiène / Propreté',
    'Autre dysfonctionnement'
  ];

  const defaultSuggestions = [
    'Amélioration du confort',
    'Ajout d\'équipements',
    'Service & Accueil',
    'Autre suggestion'
  ];

  const [customReclamationCategories, setCustomReclamationCategories] = useState(defaultReclamations);
  const [customSuggestionCategories, setCustomSuggestionCategories] = useState(defaultSuggestions);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (qrToken) {
      fetchEquipmentInfo(qrToken);
    } else {
      setLoadingInfo(false);
    }

    return () => {
      stopCameraStream();
    };
  }, [qrToken]);

  const fetchEquipmentInfo = async (token) => {
    try {
      setLoadingInfo(true);
      const response = await axios.get(`/api/qr-equipments/public/${token}`);
      if (response.data && response.data.success && response.data.equipment) {
        const eq = response.data.equipment;
        setEquipmentInfo(eq);

        const locId = eq?.location?._id || eq?.location;
        if (locId) {
          try {
            const catRes = await axios.get(`/api/qr-categories/location/${locId}`);
            if (catRes.data?.success && Array.isArray(catRes.data.categories) && catRes.data.categories.length > 0) {
              const recCats = catRes.data.categories.filter(c => c.type === 'reclamation').map(c => c.name);
              const sugCats = catRes.data.categories.filter(c => c.type === 'suggestion').map(c => c.name);
              if (recCats.length > 0) {
                setCustomReclamationCategories(recCats);
                setCategory(recCats[0]);
              }
              if (sugCats.length > 0) {
                setCustomSuggestionCategories(sugCats);
              }
            } else {
              setCategory(defaultReclamations[0]);
            }
          } catch (cErr) {
            setCategory(defaultReclamations[0]);
          }
        }
      } else {
        setCategory(defaultReclamations[0]);
      }
    } catch (err) {
      console.warn('Erreur chargement équipement:', err?.response?.data || err);
      setCategory(defaultReclamations[0]);
    } finally {
      setLoadingInfo(false);
    }
  };

  // --- GESTION PHOTO ---
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  // --- GESTION VIDÉO LIVE & RECORDER ---
  const startCameraStream = async (desiredFacing = facingMode) => {
    stopCameraStream();
    setVideoRecordedSuccess(false);
    try {
      setIsCameraActive(true);
      const constraints = {
        video: {
          facingMode: desiredFacing,
          width: { ideal: 640, max: 640 },
          height: { ideal: 480, max: 480 },
          frameRate: { ideal: 15, max: 20 }
        },
        audio: true
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      videoStreamRef.current = stream;
      if (videoLiveRef.current) {
        videoLiveRef.current.srcObject = stream;
      }
    } catch (err) {
      console.warn('Erreur accès caméra directe:', err);
      setIsCameraActive(false);
      const fileInput = document.getElementById('rec-video-file-input');
      if (fileInput) fileInput.click();
    }
  };

  const stopCameraStream = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach(track => track.stop());
      videoStreamRef.current = null;
    }
    setIsCameraActive(false);
    setIsRecording(false);
    setRecordSeconds(0);
  };

  const switchCameraFacing = () => {
    const nextFacing = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(nextFacing);
    startCameraStream(nextFacing);
  };

  const startRecording = () => {
    if (!videoStreamRef.current) return;
    recordedChunksRef.current = [];
    setVideoRecordedSuccess(false);

    let options = { 
      videoBitsPerSecond: 250000,
      audioBitsPerSecond: 32000
    };

    if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
      options.mimeType = 'video/webm;codecs=vp8,opus';
    } else if (MediaRecorder.isTypeSupported('video/webm')) {
      options.mimeType = 'video/webm';
    } else if (MediaRecorder.isTypeSupported('video/mp4')) {
      options.mimeType = 'video/mp4';
    }

    try {
      const recorder = new MediaRecorder(videoStreamRef.current, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const mimeType = recorder.mimeType || 'video/webm';
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `video-${Date.now()}.${ext}`, { type: mimeType });

        setVideoFile(file);
        setVideoPreviewUrl(URL.createObjectURL(blob));
        setVideoDuration(recordSeconds || MAX_VIDEO_SECONDS);
        setVideoRecordedSuccess(true);

        stopCameraStream();
      };

      recorder.start(500);
      setIsRecording(true);
      setRecordSeconds(0);

      const startTime = Date.now();
      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.min(MAX_VIDEO_SECONDS, Math.floor((Date.now() - startTime) / 1000));
        setRecordSeconds(elapsed);
        if (elapsed >= MAX_VIDEO_SECONDS) {
          stopRecording();
        }
      }, 250);

    } catch (err) {
      console.error('Erreur démarrage enregistrement:', err);
      alert('Impossible d\'enregistrer la vidéo.');
      stopCameraStream();
    }
  };

  const stopRecording = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) {
        alert('La vidéo est trop volumineuse (max 25 Mo).');
        return;
      }
      setVideoFile(file);
      setVideoPreviewUrl(URL.createObjectURL(file));
      setVideoDuration(0);
      setVideoRecordedSuccess(true);
    }
  };

  const handleRemoveVideo = () => {
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setVideoDuration(0);
    setVideoRecordedSuccess(false);
  };

  // --- SOUMISSION FORMULAIRE ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) {
      alert('Veuillez sélectionner un motif.');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      if (qrToken) formData.append('qrToken', qrToken);
      
      const locId = equipmentInfo?.location?._id || (typeof equipmentInfo?.location === 'string' ? equipmentInfo.location : '');
      if (locId) formData.append('locationId', locId);
      
      const eqId = equipmentInfo?._id || '';
      if (eqId) formData.append('equipmentId', eqId);

      formData.append('type', type);
      formData.append('category', category);
      formData.append('description', description);
      formData.append('clientName', clientName);
      formData.append('clientPhone', clientPhone);
      formData.append('videoDuration', videoDuration || 0);

      if (photoFile) {
        formData.append('photo', photoFile);
      }
      if (videoFile) {
        formData.append('video', videoFile);
      }

      const response = await axios.post('/api/reclamations/public', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data && response.data.success) {
        setSubmitted(true);
      } else {
        alert(response.data?.message || 'Erreur lors de l\'envoi.');
      }
    } catch (err) {
      console.error('Erreur soumission réclamation:', err);
      const message = err.response?.data?.message || 'Erreur lors de l\'envoi de votre signalement.';
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInfo) {
    return (
      <main className="rec-green-layout">
        <header className="rec-top-appbar">
          <div className="rec-appbar-title">Assistance Client</div>
        </header>
        <section className="rec-loading-container">
          <div className="rec-green-spinner"></div>
          <p>Chargement du formulaire...</p>
        </section>
      </main>
    );
  }

  if (submitted) {
    return (
      <main className="rec-green-layout">
        <header className="rec-top-appbar">
          <div className="rec-appbar-title">Confirmation</div>
        </header>

        <div className="rec-content-body">
          <article className="rec-white-card text-center p-4">
            <div className="rec-success-circle">
              <span>✓</span>
            </div>
            <h2 className="rec-success-title">Signalement Transmis !</h2>
            <p className="rec-success-text">
              Votre demande a bien été enregistrée. Notre équipe technique prend en charge votre réclamation dans les plus brefs délais.
            </p>
            <button 
              type="button"
              className="rec-green-unified-btn mt-3" 
              onClick={() => {
                setSubmitted(false);
                setDescription('');
                handleRemovePhoto();
                handleRemoveVideo();
              }}
            >
              + Nouveau Signalement
            </button>
          </article>
        </div>
      </main>
    );
  }

  return (
    <main className="rec-green-layout">
      {/* ── BARRE SUPÉRIEURE VERTE NATIVE ── */}
      <header className="rec-top-appbar">
        <div className="rec-appbar-left">
          <button type="button" className="rec-appbar-btn" onClick={() => window.history.back()} title="Retour">
            ‹
          </button>
        </div>
        <div className="rec-appbar-center">
          <span className="rec-appbar-title">Assistance & Signalements</span>
        </div>
        <div className="rec-appbar-right">
          <span className="rec-appbar-icon">👁</span>
        </div>
      </header>

      {/* ── CORPS DU FORMULAIRE ── */}
      <div className="rec-content-body">
        {/* Carte 1 : En-tête de Localisation & Équipement */}
        {equipmentInfo && (
          <section className="rec-white-card rec-info-card">
            <div className="rec-card-header-row">
              <div className="rec-card-title-lg">Emplacement Concerné</div>
              <span className="rec-green-badge">Actif</span>
            </div>
            <div className="rec-info-details">
              <div className="rec-info-item">
                <span className="rec-info-label">Site / Localisation</span>
                <span className="rec-info-val text-green">📍 {equipmentInfo.location?.name || 'Notre Espace'}</span>
              </div>
              <div className="rec-info-item">
                <span className="rec-info-label">Équipement / Poste</span>
                <span className="rec-info-val">🛋️ {equipmentInfo.name}</span>
              </div>
            </div>
          </section>
        )}

        <form onSubmit={handleSubmit} className="rec-form-container">
          {/* Carte 2 : Type de demande */}
          <section className="rec-white-card">
            <div className="rec-card-title">Type de Demande</div>
            <div className="rec-buttons-row">
              <button
                type="button"
                className={`rec-green-unified-btn ${type === 'reclamation' ? 'active-solid' : 'inactive-outline'}`}
                onClick={() => {
                  setType('reclamation');
                  setCategory(customReclamationCategories[0] || '');
                }}
              >
                ⚠️ Signaler une Panne
              </button>
              <button
                type="button"
                className={`rec-green-unified-btn ${type === 'suggestion' ? 'active-solid' : 'inactive-outline'}`}
                onClick={() => {
                  setType('suggestion');
                  setCategory(customSuggestionCategories[0] || '');
                }}
              >
                💡 Suggestion
              </button>
            </div>

            {/* Motif - Menu Déroulant 100% Personnalisé en Vert */}
            <div className="rec-field-group mt-3" ref={dropdownRef}>
              <label>Motif du Signalement *</label>
              <div className="rec-custom-dropdown">
                <button
                  type="button"
                  className={`rec-custom-dropdown-trigger ${isDropdownOpen ? 'open' : ''}`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  aria-expanded={isDropdownOpen}
                >
                  <span className="rec-dropdown-selected-text">{category || 'Sélectionner un motif'}</span>
                  <span className={`rec-dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
                </button>

                {isDropdownOpen && (
                  <ul className="rec-custom-dropdown-menu">
                    {(type === 'reclamation' ? customReclamationCategories : customSuggestionCategories).map((cat, idx) => {
                      const isSelected = category === cat;
                      return (
                        <li
                          key={idx}
                          className={`rec-custom-dropdown-item ${isSelected ? 'active' : ''}`}
                          onClick={() => {
                            setCategory(cat);
                            setIsDropdownOpen(false);
                          }}
                        >
                          <span>{cat}</span>
                          {isSelected && <span className="rec-dropdown-check">✓</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </section>

          {/* Carte 3 : Photo & Vidéo */}
          <section className="rec-white-card">
            <div className="rec-card-title">Preuves Multimédia (Optionnel)</div>

            {/* Input photo caché en dehors de la grille */}
            <input
              id="rec-camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
            />

            {/* Boutons d'action Photo & Vidéo aux dimensions rigoureusement identiques */}
            <div className="rec-buttons-row">
              {/* Action Photo */}
              {!photoPreview ? (
                <button 
                  type="button" 
                  className="rec-green-unified-btn"
                  onClick={() => document.getElementById('rec-camera-input')?.click()}
                >
                  <span>📷 Prendre Photo</span>
                </button>
              ) : (
                <div className="rec-green-unified-btn media-ready">
                  <span>📷 Photo prête</span>
                  <button type="button" onClick={handleRemovePhoto} className="rec-btn-mini-close" title="Supprimer">✕</button>
                </div>
              )}

              {/* Action Vidéo */}
              {!videoPreviewUrl && !isCameraActive ? (
                <button 
                  type="button" 
                  className="rec-green-unified-btn"
                  onClick={() => startCameraStream()}
                >
                  <span>🎥 Filmer ({MAX_VIDEO_SECONDS}s)</span>
                </button>
              ) : videoPreviewUrl && !isCameraActive ? (
                <div className="rec-green-unified-btn media-ready">
                  <span>🎥 Vidéo prête</span>
                  <button type="button" onClick={handleRemoveVideo} className="rec-btn-mini-close" title="Supprimer">✕</button>
                </div>
              ) : null}
            </div>

            {/* Aperçu de la Photo */}
            {photoPreview && (
              <div className="rec-media-preview-container mt-3">
                <img src={photoPreview} alt="Aperçu Photo" className="rec-preview-img" />
                <div className="rec-preview-bar">
                  <span className="rec-preview-tag">📷 Photo prête</span>
                  <button type="button" className="rec-green-unified-btn btn-sm btn-danger-accent" onClick={handleRemovePhoto}>
                    ✕ Supprimer la photo
                  </button>
                </div>
              </div>
            )}

            {/* Enregistreur Caméra Live */}
            {isCameraActive && (
              <div className="rec-recorder-card mt-3">
                <video 
                  ref={videoLiveRef} 
                  autoPlay 
                  playsInline 
                  muted 
                  className="rec-recorder-video"
                />

                <div className="rec-recorder-header">
                  <div className={`rec-timer-badge ${isRecording ? 'active' : ''}`}>
                    <span className="rec-red-dot"></span>
                    {isRecording ? `00:0${recordSeconds} / 00:0${MAX_VIDEO_SECONDS}` : 'Caméra Prête'}
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="rec-round-btn" onClick={switchCameraFacing} title="Tourner">
                      🔄
                    </button>
                    <button type="button" className="rec-round-btn close" onClick={stopCameraStream} title="Annuler">
                      ✕
                    </button>
                  </div>
                </div>

                {isRecording && (
                  <div className="rec-recorder-progress-track">
                    <div 
                      className="rec-recorder-progress-bar" 
                      style={{ width: `${(recordSeconds / MAX_VIDEO_SECONDS) * 100}%` }}
                    ></div>
                  </div>
                )}

                <div className="rec-recorder-bottom">
                  {!isRecording ? (
                    <button 
                      type="button" 
                      className="rec-green-unified-btn w-auto px-4" 
                      onClick={startRecording}
                    >
                      <span className="rec-btn-circle"></span>
                      <span>Démarrer l'enregistrement ({MAX_VIDEO_SECONDS}s)</span>
                    </button>
                  ) : (
                    <button 
                      type="button" 
                      className="rec-green-unified-btn btn-danger-solid w-auto px-4" 
                      onClick={stopRecording}
                    >
                      <span className="rec-btn-square"></span>
                      <span>Arrêter ({recordSeconds}s)</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Aperçu de la Vidéo enregistrée */}
            {videoPreviewUrl && !isCameraActive && (
              <div className="rec-video-preview-block mt-3">
                {videoRecordedSuccess && (
                  <div className="rec-video-success-banner">
                    <span className="rec-banner-icon">✅</span>
                    <span>Séquence vidéo enregistrée avec succès ({videoDuration > 0 ? `${videoDuration}s` : 'Prête'}) !</span>
                  </div>
                )}

                <div className="rec-video-player-frame">
                  <video 
                    src={videoPreviewUrl} 
                    controls 
                    playsInline 
                    className="rec-video-element"
                  />
                </div>

                {/* Boutons d'action identiques aux autres boutons */}
                <div className="rec-buttons-row mt-2">
                  <button 
                    type="button" 
                    className="rec-green-unified-btn" 
                    onClick={() => startCameraStream()}
                  >
                    🔄 Refaire la vidéo
                  </button>
                  <button 
                    type="button" 
                    className="rec-green-unified-btn btn-danger-accent" 
                    onClick={handleRemoveVideo}
                  >
                    ✕ Supprimer
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Carte 4 : Commentaire & Description */}
          <section className="rec-white-card">
            <div className="rec-card-title">Commentaire / Remarque</div>
            <div className="rec-field-group">
              <textarea
                id="rec-description-text"
                className="rec-clean-textarea"
                rows="2"
                placeholder="Décrivez précisément votre problème ou idée..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              ></textarea>
            </div>
          </section>

          {/* Carte 5 : Coordonnées */}
          <section className="rec-white-card">
            <div className="rec-card-title">Vos Coordonnées (Facultatif)</div>
            <div className="rec-contact-grid">
              <div className="rec-field-group">
                <label htmlFor="rec-client-name">Nom complet</label>
                <input
                  id="rec-client-name"
                  type="text"
                  className="rec-clean-input"
                  placeholder="Ex: Mohamed Ali"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                />
              </div>
              <div className="rec-field-group">
                <label htmlFor="rec-client-phone">N° Téléphone</label>
                <input
                  id="rec-client-phone"
                  type="tel"
                  className="rec-clean-input"
                  placeholder="Ex: 55 123 456"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Bouton d'envoi principal */}
          <button type="submit" className="rec-green-unified-btn btn-main-submit" disabled={submitting}>
            {submitting ? (
              <span className="rec-btn-loading">
                <span className="rec-spinner-sm"></span> Transmission en cours...
              </span>
            ) : (
              <span>Envoyer le Signalement →</span>
            )}
          </button>
        </form>

        <footer className="rec-clean-footer">
          <span>TASKO POS • Système de Gestion Qualité</span>
        </footer>
      </div>
    </main>
  );
};

export default PublicReclamationForm;
